import { DragDropModule } from '@angular/cdk/drag-drop';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    Component,
    ElementRef,
    OnDestroy,
    TemplateRef,
    ViewContainerRef,
    computed,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    isInsideSyntheticMasterNavEscapeDispatch,
    isSyntheticMasterNavEscapeEvent,
} from 'app/shared/directives/master-form-key-nav.logic';
import { WorkspaceTab, WorkspaceTabsService } from './workspace-tabs.service';
import {
    WorkspaceTabRouteGroup,
    workspaceTabSubLabelFromKey,
} from './workspace-tab-module.util';
import { WorkspaceTabTooltipService } from './workspace-tab-tooltip.service';

/**
 * One strip chip with a dropdown when a display group has 2+ tabs (or forceDropdown).
 * The list is attached to the page overlay, not inside the 16px tab strip, so it stays visible.
 */
@Component({
    selector: 'app-workspace-tab-module-group',
    template: `
        <ng-template #menuPanel>
            <div class="workspace-tab-dropdown" role="menu">
                @for (t of group().tabs; track t.id; let i = $index) {
                    <button
                        type="button"
                        class="workspace-tab-menu-row"
                        role="menuitem"
                        [class.workspace-tab-menu-row-active]="activeId() === t.id"
                        [class.workspace-tab-menu-row-key]="highlightIndex() === i"
                        matTooltipPosition="right"
                        [matTooltip]="tabTooltip(t.id)"
                        [matTooltipDisabled]="!tabTooltip(t.id)"
                        (click)="onMenuRowClick($event, t)"
                    >
                        <span class="workspace-tab-menu-title">{{ menuTitle(t) }}</span>
                        <span
                            class="workspace-tab-menu-close"
                            aria-label="Close tab"
                            (click)="onCloseClick($event, t)"
                        >
                            <mat-icon>close</mat-icon>
                        </span>
                    </button>
                }
            </div>
        </ng-template>
        <div
            class="workspace-tab workspace-tab-group"
            cdkDrag
            cdkDragPreviewContainer="parent"
            [cdkDragConstrainPosition]="keepTabDragOnStrip"
            [class.workspace-tab-active]="isGroupActive()"
            role="presentation"
            (cdkDragStarted)="onChipDrag(true)"
            (cdkDragMoved)="onChipDragMoved()"
            (cdkDragEnded)="onChipDrag(false)"
        >
            <button
                #trigger
                type="button"
                cdkDragHandle
                class="workspace-tab-group-trigger"
                [attr.aria-expanded]="menuOpen()"
                [attr.aria-label]="'Open ' + groupTriggerLabel()"
                matTooltipPosition="below"
                [matTooltip]="groupChipTooltip()"
                [matTooltipDisabled]="!groupChipTooltip()"
                (mouseenter)="onTriggerEnter()"
                (mouseleave)="onTriggerLeave()"
                (click)="toggleMenu()"
            >
                <span class="workspace-tab-group-label">{{ groupTriggerLabel() }}</span>
                <mat-icon class="workspace-tab-group-chevron">arrow_drop_down</mat-icon>
            </button>
            <div class="workspace-tab-drag-preview" *cdkDragPreview>{{ groupTriggerLabel() }}</div>
        </div>
    `,
    styleUrl: './workspace-tabs-bar.component.scss',
    imports: [DragDropModule, OverlayModule, MatIconModule, MatTooltipModule],
})
export class WorkspaceTabModuleGroupComponent implements OnDestroy {
    readonly group = input.required<WorkspaceTabRouteGroup>();
    private readonly _workspaceTabs = inject(WorkspaceTabsService);
    private readonly _tabTooltips = inject(WorkspaceTabTooltipService);
    private readonly _host = inject(ElementRef<HTMLElement>);
    readonly keepTabDragOnStrip = this._workspaceTabs.keepTabDragOnStrip;
    private readonly _overlay = inject(Overlay);
    private readonly _viewContainerRef = inject(ViewContainerRef);
    private readonly _menuPanel = viewChild.required<TemplateRef<unknown>>('menuPanel');
    private readonly _trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');
    private _overlayRef: OverlayRef | null = null;
    private _closeTimer: ReturnType<typeof setTimeout> | null = null;
    /** Closes a list opened by landing on the main chip, if it is not used. */
    private _focusIdleTimer: ReturnType<typeof setTimeout> | null = null;
    private _hoverOpenedAt = 0;
    /** Up/Down has moved inside this list, so Enter belongs to a sub-tab. */
    private _arrowNavigated = false;
    private _removeOutsideListener: (() => void) | null = null;
    private _dragMoved = false;

    readonly menuOpen = signal(false);
    /** Row marked by Alt+Down / Alt+Up. -1 until the keyboard opens the list. */
    readonly highlightIndex = signal(-1);
    readonly activeId = computed(() => this._workspaceTabs.activeTabId());

    readonly isGroupActive = computed(() => {
        const id = this.activeId();
        return id != null && this.group().tabs.some((t) => t.id === id);
    });

    readonly groupTriggerLabel = computed(() => {
        const g = this.group();
        if (g.forceDropdown) {
            return g.groupLabel;
        }
        const id = this.activeId();
        const active = (id != null ? g.tabs.find((t) => t.id === id) : undefined) ?? g.tabs[0];
        return active?.title?.trim() || g.groupLabel;
    });

    ngOnDestroy(): void {
        this.clearCloseTimer();
        this.clearFocusIdleTimer();
        this.closeMenu();
    }

    /** A chip with a dropdown opens when the pointer rests on it. */
    onChipDrag(active: boolean): void {
        this._workspaceTabs.tabStripDragging.set(active);
        if (active) {
            this._dragMoved = false;
            this.closeMenu();
            const strip = this._host.nativeElement.closest('.workspace-tabs-scroll');
            this._workspaceTabs.rememberTabDragY(strip?.getBoundingClientRect().top ?? 0);
            return;
        }
        setTimeout(() => {
            this._dragMoved = false;
        }, 0);
    }

    onChipDragMoved(): void {
        this._dragMoved = true;
    }

    onTriggerEnter(): void {
        if (this._workspaceTabs.tabStripDragging()) {
            return;
        }
        this.clearCloseTimer();
        this.clearFocusIdleTimer();
        if (this._overlayRef) {
            return;
        }
        this.openMenu();
        this._hoverOpenedAt = Date.now();
    }

    onTriggerLeave(): void {
        this.scheduleClose();
    }

    menuTitle(tab: WorkspaceTab): string {
        if (tab.reportWindowKey) {
            return tab.title;
        }
        if (tab.titleOverride) {
            return tab.titleOverride;
        }
        if (this.group().forceDropdown) {
            return workspaceTabSubLabelFromKey(tab.tabKey);
        }
        return tab.title;
    }

    /** Form summary for the sub-tab row; blank when the form has no important values. */
    tabTooltip(tabId: string): string {
        return this._tabTooltips.textFor(tabId);
    }

    /** Tooltip on the group chip: active sub-tab summary, else first tab with data. */
    groupChipTooltip(): string {
        const tabs = this.group().tabs;
        const active = this.activeId();
        if (active) {
            const text = this._tabTooltips.textFor(active);
            if (text) {
                return text;
            }
        }
        for (const tab of tabs) {
            const text = this._tabTooltips.textFor(tab.id);
            if (text) {
                return text;
            }
        }
        return '';
    }

    toggleMenu(): void {
        if (this._dragMoved) {
            this._dragMoved = false;
            return;
        }
        // The click that follows a hover-open should leave the list open.
        if (this._overlayRef && Date.now() - this._hoverOpenedAt < 400) {
            return;
        }
        if (this._overlayRef) {
            this.closeMenu();
            return;
        }
        this.openMenu();
    }

    private openMenu(): void {
        if (this._overlayRef) {
            return;
        }
        const origin = this._trigger().nativeElement;
        const positionStrategy = this._overlay
            .position()
            .flexibleConnectedTo(origin)
            .withFlexibleDimensions(false)
            .withPush(false)
            .withViewportMargin(8)
            .withPositions([
                { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
                { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
            ]);
        const overlayRef = this._overlay.create({
            positionStrategy,
            hasBackdrop: false,
            panelClass: 'workspace-tab-dropdown-pane',
            scrollStrategy: this._overlay.scrollStrategies.reposition(),
        });
        overlayRef.attach(new TemplatePortal(this._menuPanel(), this._viewContainerRef));
        const pane = overlayRef.overlayElement;
        const onPaneEnter = (): void => {
            this.clearCloseTimer();
            this.clearFocusIdleTimer();
        };
        const onPaneLeave = (): void => this.scheduleClose();
        pane.addEventListener('mouseenter', onPaneEnter);
        pane.addEventListener('mouseleave', onPaneLeave);
        const onOutside = (event: Event): void => {
            const target = event.target;
            if (!(target instanceof Node)) {
                return;
            }
            if (origin.contains(target) || pane.contains(target)) {
                return;
            }
            this.closeMenu();
        };
        document.addEventListener('pointerdown', onOutside, true);
        this._removeOutsideListener = () => {
            document.removeEventListener('pointerdown', onOutside, true);
            pane.removeEventListener('mouseenter', onPaneEnter);
            pane.removeEventListener('mouseleave', onPaneLeave);
        };
        overlayRef.keydownEvents().subscribe((event) => {
            if (event.key !== 'Escape') {
                return;
            }
            if (
                isSyntheticMasterNavEscapeEvent(event) ||
                isInsideSyntheticMasterNavEscapeDispatch()
            ) {
                return;
            }
            this.closeMenu();
        });
        overlayRef.detachments().subscribe(() => {
            this._overlayRef = null;
            this.menuOpen.set(false);
        });
        this._overlayRef = overlayRef;
        this.menuOpen.set(true);
    }

    onMenuRowClick(event: MouseEvent, tab: WorkspaceTab): void {
        if ((event.target as HTMLElement).closest('.workspace-tab-menu-close')) {
            return;
        }
        this.closeMenu();
        void this._workspaceTabs.selectTab(tab);
    }

    onCloseClick(event: Event, tab: WorkspaceTab): void {
        event.preventDefault();
        event.stopPropagation();
        this.closeMenu();
        void this._workspaceTabs.closeTab(tab, event);
    }

    private scheduleClose(): void {
        this.clearCloseTimer();
        this._closeTimer = setTimeout(() => {
            this._closeTimer = null;
            this.closeMenu();
        }, 160);
    }

    private clearCloseTimer(): void {
        if (this._closeTimer != null) {
            clearTimeout(this._closeTimer);
            this._closeTimer = null;
        }
    }

    /** Main-chip focus opens the list. Close it after 6s unless the user uses it. */
    private armFocusIdleClose(): void {
        this.clearFocusIdleTimer();
        this._focusIdleTimer = setTimeout(() => {
            this._focusIdleTimer = null;
            this.closeMenu();
        }, 6000);
    }

    private clearFocusIdleTimer(): void {
        if (this._focusIdleTimer != null) {
            clearTimeout(this._focusIdleTimer);
            this._focusIdleTimer = null;
        }
    }

    isOpen(): boolean {
        return this._overlayRef != null;
    }

    /**
     * Enter selects a sub-tab only when this list is what the user is driving.
     * An open list must not take Enter from a page, such as a sales row.
     */
    ownsEnter(event: KeyboardEvent): boolean {
        if (this._arrowNavigated) {
            return true;
        }
        const target = event.target;
        if (!(target instanceof Node)) {
            return false;
        }
        const trigger = this._trigger()?.nativeElement;
        const pane = this._overlayRef?.overlayElement;
        return (!!trigger && trigger.contains(target)) || (!!pane && pane.contains(target));
    }

    /** Open the list and mark one row. Used when Alt+Left/Right lands on this chip. */
    openAt(index: number): void {
        const count = this.group().tabs.length;
        if (count === 0) {
            return;
        }
        this.openMenu();
        this.highlightIndex.set(Math.max(0, Math.min(index, count - 1)));
        this.armFocusIdleClose();
    }

    /** Move the marked row. Opens the list if it is still closed. */
    moveHighlight(delta: number): void {
        this._arrowNavigated = true;
        this.clearFocusIdleTimer();
        const count = this.group().tabs.length;
        if (count === 0) {
            return;
        }
        if (!this._overlayRef) {
            this.openMenu();
            this.highlightIndex.set(delta > 0 ? 0 : count - 1);
            return;
        }
        const current = this.highlightIndex();
        const from = current < 0 ? (delta > 0 ? -1 : count) : current;
        this.highlightIndex.set(Math.max(0, Math.min(count - 1, from + delta)));
    }

    /** Enter while the list is open. */
    selectHighlighted(): void {
        const tabs = this.group().tabs;
        const index = this.highlightIndex();
        const tab = tabs[index] ?? tabs[0];
        if (!tab) {
            this.closeMenu();
            return;
        }
        this.closeMenu();
        void this._workspaceTabs.selectTab(tab);
    }

    closeMenu(): void {
        this._arrowNavigated = false;
        this.highlightIndex.set(-1);
        this.clearCloseTimer();
        this.clearFocusIdleTimer();
        this._removeOutsideListener?.();
        this._removeOutsideListener = null;
        this.menuOpen.set(false);
        if (!this._overlayRef) {
            return;
        }
        const ref = this._overlayRef;
        this._overlayRef = null;
        ref.dispose();
    }
}
